import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Stack,
    Image,
    Grid,
    Link,
    Button,
    AccordionItem,
    AccordionButton,
    AccordionPanel
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {getUrl} from '../../../../utils/cloudinary-utils'
import CloudinaryImage from '../../../../components/cloudinary-media/image'
import useTagManager from '../../../../hooks/use-tag-manager'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const FAQItem = ({
    buttonText,
    titleAnchor,
    imagePosition,
    imageUrl,
    buttonStyle = 'link',
    buttonLink,
    imgAlt,
    product,
    title,
    titleColor,
    content,
    titleFontFamily,
    contentFontFamily,
    analyticsProps
}) => {
    const CTAVariant = buttonStyle === 'link' ? 'linkUnderline' : 'buttonDark'
    const tagManager = useTagManager()
    const pdTagManager = usePdTagManager()
    return (
        <AccordionItem className="faq-accordion-item">
            {({isExpanded}) => (
                <Box id={titleAnchor?.replaceAll(' ', '')} className="faq-accordion-item-box">
                    <Text as="h4" className="faq-accordion-text">
                        <AccordionButton
                            className="faq-accordion-item-button"
                            onClick={() => {
                                if (!isExpanded) {
                                    tagManager.pdpCtaClick(
                                        'cta click - accordion expand - ' + title,
                                        product?.name
                                    )

                                    pdTagManager.selectComponent({
                                        componentId: analyticsProps?.componentType || '',
                                        componentName: analyticsProps?.componentName || '',
                                        titleName: title,
                                        titlePosition: analyticsProps?.index
                                    })
                                }
                            }}
                        >
                            <Box
                                as="span"
                                color={titleColor}
                                fontSize="23px"
                                className="faq-accordion-item-button-box"
                                fontFamily={titleFontFamily}
                                fontWeight="500"
                            >
                                {title}
                            </Box>

                            {isExpanded ? (
                                <Image
                                    alt="Expand Icon"
                                    width="25px"
                                    className="faq-accordion-item-button-image"
                                    height="25px"
                                    color="black"
                                    verticalAlign="middle"
                                    src={getUrl('UIImages/images/minus-large.svg')}
                                />
                            ) : (
                                <Image
                                    alt="Expand Icon"
                                    width="25px"
                                    className="faq-accordion-item-button-image"
                                    height="25px"
                                    color="black"
                                    verticalAlign="middle"
                                    src={getUrl('UIImages/images/plus-large.svg')}
                                />
                            )}
                        </AccordionButton>
                    </Text>

                    <AccordionPanel className="faq-accordion-panel">
                        <Grid gap="20px" className="faq-accordion-panel-grid">
                            {imageUrl && (
                                <Box className="faq-accordion-panel-grid-box">
                                    <CloudinaryImage
                                        src={imageUrl}
                                        className="faq-accordion-panel-image"
                                        imgSizes={{base: '100vw', xl: '1100px'}}
                                        sizes={[320, 374, 435, 780, 1100]}
                                        alt={imgAlt}
                                    />
                                </Box>
                            )}

                            <Stack
                                gridArea={imagePosition === 'top' ? 'auto' : '1 / 1'}
                                gap="20px"
                                className="faq-accordion-panel-grid-stack"
                            >
                                <Box
                                    sx={{
                                        a: {
                                            textDecoration: 'underline',
                                            _hover: {
                                                textDecoration: 'none'
                                            }
                                        }
                                    }}
                                    className="faq-grid-stack-box"
                                    dangerouslySetInnerHTML={{__html: content}}
                                    fontSize="14px"
                                    fontFamily={contentFontFamily}
                                />

                                {buttonLink && buttonText ? (
                                    <Box className="faq-grid-stack-button-box">
                                        <Button
                                            className="faq-grid-stack-button"
                                            as={Link}
                                            href={buttonLink}
                                            padding={
                                                CTAVariant === 'linkUnderline'
                                                    ? '0px !important'
                                                    : '4px 25px !important'
                                            }
                                            variant={CTAVariant}
                                            size="small"
                                            paddingLeft="inherit"
                                        >
                                            {buttonText}
                                        </Button>
                                    </Box>
                                ) : null}
                            </Stack>
                        </Grid>
                    </AccordionPanel>
                </Box>
            )}
        </AccordionItem>
    )
}

FAQItem.propTypes = {
    buttonText: PropTypes.string,
    titleAnchor: PropTypes.string,
    imageCustomField: PropTypes.string,
    product: PropTypes.shape({
        name: PropTypes.string
    }),
    imagePosition: PropTypes.string,
    imageUrl: PropTypes.string,
    buttonStyle: PropTypes.string,
    buttonLink: PropTypes.string,
    imgAlt: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleColor: PropTypes.string,
    content: PropTypes.string.isRequired,
    contentFontFamily: PropTypes.string,
    titleFontFamily: PropTypes.string,
    analyticsProps: PropTypes.shape({
        componentType: PropTypes.string,
        componentName: PropTypes.string,
        index: PropTypes.number
    })
}

export default FAQItem
